/**
 * The one error renderer (§10.4). Every failure in the app arrives here as a
 * problem+json document; nothing else formats an API failure.
 *
 * `category` on a moderation block is surfaced explicitly, because "blocked by
 * content policy" without the category is the generic failure §7.3 forbids.
 */

import type { ReactNode } from 'react';
import { problemSlug, type Problem } from '../api/problem';
import { humanize } from '../lib/format';

function toneFor(status: number): 'bad' | 'warn' {
  return status >= 500 || status === 0 ? 'bad' : 'warn';
}

export function ErrorView({
  problem,
  onRetry,
  children,
}: {
  problem: Problem;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  const slug = problemSlug(problem);
  const retryable = slug !== 'moderation-blocked' && slug !== 'forbidden' && Boolean(onRetry);

  return (
    <div className={`banner banner-${toneFor(problem.status)}`} role="alert">
      <div className="banner-text">
        <strong className="banner-title">{problem.title}</strong>
        {problem.detail ? <div className="banner-body">{problem.detail}</div> : null}

        {slug === 'moderation-blocked' && problem.category ? (
          <p className="banner-body">
            Category: <strong>{humanize(problem.category)}</strong>. A moderation block cannot be
            retried.
          </p>
        ) : null}

        {slug === 'rate-limited' && typeof problem.retry_after === 'number' ? (
          <p className="banner-body">Try again in {problem.retry_after}s.</p>
        ) : null}

        {slug === 'adapter-unavailable' && problem.platform ? (
          <p className="banner-body">{humanize(problem.platform)} is not reachable right now.</p>
        ) : null}

        {children}
        <p className="banner-meta">
          <code>{slug}</code>
          {problem.status ? ` · ${problem.status}` : ''}
        </p>
      </div>
      {retryable ? (
        <div className="banner-actions">
          <button type="button" className="btn btn-ghost" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Inline variant for form-level failures where a full banner is too heavy. */
export function InlineError({ problem }: { problem: Problem }) {
  return (
    <p className="field-error" role="alert">
      {problem.detail ?? problem.title}
      {problem.category ? ` (category: ${humanize(problem.category)})` : ''}
    </p>
  );
}
